/**
 * Channel profile rules, shared by the API (which enforces them) and the web app (which checks as
 * you type). A profile is a display name, a profile photo, a short bio and a few links.
 */

export const BIO_MAX_LENGTH = 300;
export const MAX_PROFILE_LINKS = 5;
export const LINK_MAX_LENGTH = 300;
/** Profile photos are cropped and resized in the browser; the API refuses anything bigger. */
export const PHOTO_MAX_BYTES = 1024 * 1024;
export const PHOTO_SIZE_PX = 512;

/**
 * Plain text with its line breaks kept: Windows line endings unified, tabs as spaces, control and
 * text-direction characters removed (they can disguise what a bio says), trailing spaces trimmed,
 * at most one blank line in a row.
 */
export function normalizeBio(input: string): string {
  return input
    .replace(/\r\n?/g, '\n')
    .replace(/\t/g, ' ')
    .replace(/[\u0000-\u0008\u000B-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g, '')
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export type LinkCheck =
  | { ok: true; url: string; label: string }
  | { ok: false; reason: 'invalid' | 'not_https' | 'too_long' };

/**
 * A profile link: https only, a real domain name (no IP addresses, ports or user:password@), shown
 * by its hostname. Returns the canonical form to store.
 */
export function checkProfileLink(input: string): LinkCheck {
  const raw = input.trim();
  if (raw.length > LINK_MAX_LENGTH) return { ok: false, reason: 'too_long' };
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { ok: false, reason: 'invalid' };
  }
  if (url.protocol !== 'https:') return { ok: false, reason: 'not_https' };
  const host = url.hostname.toLowerCase();
  if (url.username || url.password || url.port) return { ok: false, reason: 'invalid' };
  // A dotted domain name with a letter in its last label: rules out IPv4/IPv6 literals and "localhost".
  if (!/^([a-z0-9-]+\.)+[a-z0-9-]*[a-z][a-z0-9-]*$/.test(host)) return { ok: false, reason: 'invalid' };
  if (url.href.length > LINK_MAX_LENGTH) return { ok: false, reason: 'too_long' };
  return { ok: true, url: url.href, label: linkLabel(url.href) };
}

/** What a link is shown as: its hostname without www. */
export function linkLabel(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return href;
  }
}

/** A stored profile link and the hostname it is shown as. */
export interface ProfileLink {
  url: string;
  label: string;
}

/** Public channel profile, as the channel page and the creator strip under a stream show it. */
export interface ChannelProfile {
  displayName: string | null;
  handle: string;
  bio: string | null;
  links: ProfileLink[];
  /** Square 512 px image on the media CDN, or null for the round-initial fallback. */
  photoUrl: string | null;
}
